import { Component, computed, effect, inject, untracked } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { CustomerService, InsurenetInterfaceService } from '@core';
import { OrderStepBaseComponent } from '../../order-step-base-component';
import { PriceService } from '../../price/price.service';
import { OrderQuery } from '../../state/order.query';
import { OrderService } from '../../state/order.service';
import { OrderState } from 'app/order/models/order-state.model';
import { OrderStepsService } from 'app/order/order-step.service';
import { OrderStore } from 'app/order/state/order.store';
import { format } from 'date-fns';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-submit',
    templateUrl: './submit.component.html',
    styleUrls: ['./submit.component.scss'],
    standalone: false,
})
export class SubmitComponent extends OrderStepBaseComponent<Pick<OrderState, 'contract'>> {
    private orderStepService = inject(OrderStepsService);
    private customerService = inject(CustomerService);
    private orderStore = inject(OrderStore);
    protected query = inject(OrderQuery);

    protected advice = toSignal(this.query.state$.pipe(map((state) => state.advice)));

    public discountMultiplier = 1;

    private requestPending: boolean = false;
    protected showInvoiceNotice: boolean = false;
    protected consultationProtocolPending: boolean = false;

    public constructor(
        public priceService: PriceService,
        public orderService: OrderService,
        private formBuilder: FormBuilder,
        private insurenetInterface: InsurenetInterfaceService,
    ) {
        super();
        this.initForm();
        this.subscribeToDiscountCode();

        this.orderService.requestPending$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((value) => (this.requestPending = value));

        // reset control when user changes the advice object
        effect(() => {
            this.advice();

            untracked(() => {
                this.form.controls.tos.setValue(false);
            });
        });
    }

    public handleStateChange(): void {
        if (!this.product()?.orderConfig?.requiresMedicalData) {
            this.form.get('medicalData').disable();
        }
        if (!this.product()?.orderConfig?.requiresNoPriorDefect) {
            this.form.get('noPriorDefect').disable();
            this.showInvoiceNotice = false;
        } else {
            this.showInvoiceNotice = true;
        }
    }

    public login(): void {}

    public getData(): Partial<OrderState> {
        return { contract: { ...this.state.contract, discountCode: this.form.get('discountCode').value } };
    }

    private initForm(): void {
        this.form = this.formBuilder.group({
            tos: [false, Validators.requiredTrue],
            medicalData: [false, Validators.requiredTrue],
            noPriorDefect: [false, Validators.requiredTrue],
            contact: [false],
            discountCode: [''],
        });
    }

    private subscribeToDiscountCode(): Subscription {
        return this.form
            .get('discountCode')
            .valueChanges.pipe(
                distinctUntilChanged(),
                switchMap((discountCode) => this.insurenetInterface.getDiscount(discountCode, this.insuranceType().id)),
            )
            .subscribe((discountMultiplier) => (this.discountMultiplier = discountMultiplier));
    }

    protected showConsultationProtocol = computed<boolean>(() => (this.advice().mode === 'WAIVER' ? false : true));

    protected downloadConsultationProtocol(): void {
        if (this.consultationProtocolPending) {
            return;
        }

        this.orderService.update(this.state);
        const order = this.orderStore.getValue();

        this.consultationProtocolPending = true;

        this.insurenetInterface
            .getConsultationProtocol(order, this.product(), this.insuranceType(), this.customerService.customer)
            .pipe(finalize(() => (this.consultationProtocolPending = false)))
            .subscribe((pdfBlob) => {
                this.triggerBrowserDownload(pdfBlob);
            });
    }

    private triggerBrowserDownload(blob: Blob): void {
        const pdfBlob = blob.type ? blob : new Blob([blob], { type: 'application/pdf' });

        const url = window.URL.createObjectURL(pdfBlob);
        const anchor = document.createElement('a');

        anchor.href = url;
        anchor.download = this.getProtocolFileName();
        anchor.rel = 'noopener';

        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();

        setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    }

    private getProtocolFileName(): string {
        return `${format(new Date(), 'yyyyMMdd')}_Beratungsprotokoll_${this.product().title.replace(' ', '_')}.pdf`;
    }

    public get someStepsInvalid(): boolean {
        return this.orderStepService.invalidSteps().length > 0;
    }

    public override get isInvalid(): boolean {
        return false;
    }

    public override submit(): void {
        if (this.form.invalid || this.someStepsInvalid || this.requestPending) {
            return;
        }

        this.next.emit(this.getData());
    }
}
